/**
 * FlowEdge.tsx — a custom React Flow edge that colours itself by FlowKind
 * and animates dashes along its length to represent live flow.
 *
 * The dash animation speed is proportional to the current flow magnitude
 * (read from the edge's `data.flow`), so a busy edge moves fast and a quiet
 * one crawls — the "flow of goods/money" visual the spec asks for. Phase 4
 * will reuse `data.slowdown` (set when the source node is flagged a
 * bottleneck) to damp the animation further.
 *
 * Weight label sits at the edge midpoint; colour legend lives in the sidebar.
 */
import { memo, useLayoutEffect, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';

import { flowKindColor } from './palette';
import { bendPath } from './bendPath';
import { formatNum } from './format';
import type { Bend, GraphEdge } from '../engine/types';

/** Compact "how this edge gets its value" hint, computed by the canvas where
 *  the authored GraphEdge lives and rendered under the flow value:
 *
 *  - weight fan-out (no typed connections):  `w=0.8` — the flow is the
 *    source node's outflow × this weight, normalised across the source's
 *    out-edges (in probability terms, exactly P(v|u));
 *  - typed connections:                       `ƒ derive x * params.p` —
 *    the flow is the connection's CEL transform of its source value;
 *  - signal edges carry no flow:              `signal`.
 */
export function edgeDerivation(e: GraphEdge): string {
  if (e.flowKind === 'signal') return 'signal';
  const conns = e.connections ?? [];
  if (conns.length === 0) return `w=${formatNum(e.weight)}`;
  return conns
    .map((c) => {
      const expr = c.expr && c.expr !== 'x' ? ' ' + c.expr : '';
      const shown = expr.length > 18 ? expr.slice(0, 17) + '…' : expr;
      return `ƒ ${c.kind}${shown}`;
    })
    .join(', ');
}

/** The full-sentence tooltip version of the same hint. */
export function edgeDerivationTitle(e: GraphEdge): string {
  if (e.flowKind === 'signal') return 'Signal edge — carries a value, no flow. Double-click to inspect.';
  const conns = e.connections ?? [];
  if (conns.length === 0)
    return `Flow = source node's outflow × weight ${formatNum(e.weight)} (sibling weights are normalised). Double-click to inspect.`;
  return `Flow derived from ${conns.length} typed connection${conns.length > 1 ? 's' : ''} (${conns.map((c) => c.kind).join(', ')}). Double-click to inspect.`;
}

/**
 * A draggable endpoint indicator rendered at the source/target of an edge.
 * React Flow's edge-reconnect reads `data-edgeid` + `data-handlepos` +
 * `data-handleid` from the element under the cursor, so a div positioned at
 * the edge's endpoint with those attributes becomes a grab handle. Dragging
 * it fires `onReconnect` on the `<ReactFlow>` element.
 *
 * Used to move an existing edge from one side of a node to another (or to a
 * different node entirely) without redrawing it.
 */
function ReconnectHandle({
  edgeId,
  handleType,
  handleId,
  x,
  y,
  color,
  editable,
}: {
  edgeId: string;
  /** 'source' | 'target' — which end this handle re-anchors. */
  handleType: 'source' | 'target';
  handleId: string | null | undefined;
  x: number;
  y: number;
  color: string;
  editable: boolean;
}): JSX.Element {
  if (!editable) return <></>;
  return (
    <div
      className="edge-reconnect-handle"
      // RF's reconnect walker reads these three attributes to know which edge
      // + which endpoint is being dragged. `data-handlepos` is required.
      data-edgeid={edgeId}
      data-handlepos={handleType === 'source' ? 'source' : 'target'}
      data-handleid={handleId ?? ''}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)',
        background: 'var(--panel)',
        borderColor: color,
      }}
      title={`Drag to re-anchor the ${handleType}`}
    />
  );
}

export interface FlowEdgeData {
  flowKind: string;
  customKind?: string;
  customColor?: string;
  weight: number;
  /** Current flow magnitude along this edge (from the tick's edgeFlows). */
  flow?: number;
  /** Compact derivation hint (from `edgeDerivation`): how this edge's flow
   *  is computed — 'w=0.8' (fan-out weight) or 'ƒ derive x * params.p'
   *  (typed connection). Rendered as a muted second line under the value. */
  derivation?: string;
  /** Full-sentence tooltip version (`edgeDerivationTitle`). */
  derivationTitle?: string;
  /** True when the displayed flows come from an external model (api/relay). */
  flowsFromModel?: boolean;
  /** Phase 4: when true (source node is a bottleneck), damp the animation. */
  slowdown?: boolean;
  /** Whether the sim is ticking. When false the dash animation is suppressed
   *  so a paused edge looks still (the flow value is the last tick's, which
   *  would otherwise keep the dashes moving). */
  running?: boolean;
  /** #4 — Group highlight state: this edge is in a highlighted/hovered group. */
  glow?: boolean;
  /** #4 — Another group is highlighted, so this edge dims to recede. */
  dim?: boolean;
  /** Click handler — selects the edge (highlight). Does NOT open the modal. */
  onSelect?: (edgeId: string) => void;
  /** Double-click handler — opens the edge modal. */
  onOpen?: (edgeId: string) => void;
  /** Right-click handler — opens the context menu. */
  onContext?: (edgeId: string, x: number, y: number) => void;
  isAuto?: boolean;
  computedSourceHandle?: string;
  computedTargetHandle?: string;
  onPromoteEdge?: (id: string, sourceHandle: string | undefined, targetHandle: string | undefined, bends: Bend[]) => void;
  /** When true, both endpoints are draggable to re-anchor to another node
   *  side (React Flow's edge-reconnect). Set by the canvas on hover/select. */
  editable?: boolean;
  /** User-authored bend points along the edge. When present, the path is
   *  rebuilt from src → bends → tgt via bendPath (each bend's mode controls
   *  the curve at that point). */
  bends?: Bend[];
  /** Position of the weight label along the curve, t ∈ [0,1] (0.5 = middle of
   *  arc length). Drag the label to change it; resolves to (x, y) via
   *  getPointAtLength on the rendered <path>. */
  labelT?: number;
  /** Move an existing bend (drag). */
  onMoveBend?: (edgeId: string, idx: number, x: number, y: number, fallbackBends?: Bend[]) => void;
  /** Right-click a bend → open the bend context menu (sharp/smooth/default/delete). */
  onBendContext?: (edgeId: string, idx: number, x: number, y: number) => void;
  /** Drag the weight label → update its t along the curve. */
  onMoveLabel?: (edgeId: string, t: number) => void;
  // React Flow's Edge<T> requires T extends Record<string, unknown>.
  [key: string]: unknown;
}

const FlowEdgeBase = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  targetHandleId,
  data,
  selected,
  markerEnd,
  markerStart,
}: EdgeProps & { data?: FlowEdgeData }) => {
  const d = data ?? ({} as FlowEdgeData);
  const color = flowKindColor(d.flowKind as never, d.customKind, d.customColor);
  // Read the viewport (x, y, zoom) so the bend-drag handler can convert screen
  // coords → flow coords. RF recomputes this on pan/zoom; we read it lazily in
  // the pointermove handler.
  const rf = useReactFlow();
  // Path: use bendPath when bends exist, otherwise RF's getBezierPath (so an
  // unbent edge looks identical to before).
  const bends = d.bends ?? [];
  const { d: bentPath, labelX: bendLabelX, labelY: bendLabelY } =
    bends.length > 0
      ? bendPath(
          { x: sourceX, y: sourceY },
          { x: targetX, y: targetY },
          sourcePosition,
          targetPosition,
          bends,
        )
      : { d: '', labelX: 0, labelY: 0 };
  const [bezierPath, labelXBezier, labelYBezier] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const path = bends.length > 0 ? bentPath : bezierPath;
  const fallbackLabelX = bends.length > 0 ? bendLabelX : labelXBezier;
  const fallbackLabelY = bends.length > 0 ? bendLabelY : labelYBezier;

  // Resolve the label's t-parameter to actual (x, y) on the rendered path.
  // getPointAtLength needs the SVG <path> element (rendered by BaseEdge with
  // id=edgeId). Falls back to the geometric midpoint while the path mounts.
  const labelT = d.labelT ?? 0.5;
  const [labelXY, setLabelXY] = useState<{ x: number; y: number }>({
    x: fallbackLabelX,
    y: fallbackLabelY,
  });
  useLayoutEffect(() => {
    const el = document.getElementById(id) as SVGPathElement | null;
    if (!el || typeof el.getTotalLength !== 'function') {
      setLabelXY({ x: fallbackLabelX, y: fallbackLabelY });
      return;
    }
    const total = el.getTotalLength();
    if (total <= 0) {
      setLabelXY({ x: fallbackLabelX, y: fallbackLabelY });
      return;
    }
    const pt = el.getPointAtLength(labelT * total);
    setLabelXY({ x: pt.x, y: pt.y });
  }, [id, labelT, path, fallbackLabelX, fallbackLabelY]);
  const labelX = labelXY.x;
  const labelY = labelXY.y;

  // Animation duration: inversely proportional to flow magnitude. A still
  // edge (flow=0) has no animation; a busy one (flow=80) animates fast. The
  // bottleneck slowdown multiplier (Phase 4) stretches the duration further.
  // Suppressed entirely when the sim is paused (`running` false) — otherwise
  // the dashes keep marching along using the last tick's flow value, which
  // makes a paused graph look like it's still running. The dash pattern
  // itself stays (a paused edge reads as stationary dashes, a no-flow edge as
  // a solid line), only the motion stops.
  const flow = d.flow ?? 0;
  const flowing = flow > 0;
  const marching = d.running !== false && flowing;
  const baseDur = flowing ? Math.max(0.6, 4 - flow * 0.04) : 0; // seconds
  const dur = marching ? (d.slowdown ? baseDur * 2.5 : baseDur) : 0;
  // #4 — Group highlight: a lit edge gets a wider stroke + soft glow; dimmed
  // edges drop to low opacity. Both are applied via CSS class so transitions
  // animate smoothly. strokeWidth grows for glow but stays readable.
  const widthClass = d.glow ? ' glow' : d.dim ? ' dim' : '';

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        // Forward the marker props from the edge object so RF attaches the
        // arrowhead defs (set by GraphCanvas from `showArrows` + edge colour).
        markerStart={markerStart}
        markerEnd={markerEnd}
        className={`flow-edge-path${widthClass}`}
        style={{
          stroke: color,
          strokeWidth: selected ? 3 : d.glow ? 4 : 2,
          strokeDasharray: dur > 0 ? '6 6' : undefined,
          // CSS animation on the dash offset — moves the dashes along the path.
          animation: dur > 0 ? `flowdash ${dur.toFixed(2)}s linear infinite` : undefined,
          opacity: d.dim ? 0.18 : 1,
        }}
      />
      <EdgeLabelRenderer>
        <button
          type="button"
          className="flow-edge-label nodrag nopan"
          style={{
            position: 'absolute',
            // The label sits at (labelX, labelY) — resolved from `labelT`
            // against the rendered <path>'s arc length. Drag updates `labelT`.
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            color,
            borderColor: color,
            background: selected ? color : 'var(--panel)',
            cursor: d.editable ? 'grab' : 'pointer',
          }}
          // Modal is reserved for LABEL DOUBLE-CLICK only. Single-click does
          // nothing (the drag handler below owns pointerdown for label-moving).
          // stopPropagation on dblclick so the edge-body's onEdgeDoubleClick
          // (which adds a bend) does NOT fire when the user dblclicks the label.
          onClick={(e) => {
            // Single-click selects the edge (highlight) but does NOT open the
            // modal — that's reserved for double-click.
            e.stopPropagation();
            d.onSelect?.(id);
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            d.onOpen?.(id);
          }}
          // Drag the label along the curve: convert pointer coords to the
          // closest point on the path, then to a t-parameter via getPointAtLength.
          onPointerDown={(e) => {
            if (!d.editable || !d.onMoveLabel) return;
            e.stopPropagation();
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            const pathEl = document.getElementById(id) as SVGPathElement | null;
            if (!pathEl || typeof pathEl.getTotalLength !== 'function') return;
            const total = pathEl.getTotalLength();
            if (total <= 0) return;
            // Find the t whose path point is closest to the pointer (flow coords).
            // Sample 40 points along the path; cheap + smooth enough at this scale.
            const closestT = (clientX: number, clientY: number): number => {
              const p = rf.screenToFlowPosition({ x: clientX, y: clientY });
              let best = 0;
              let bestD = Infinity;
              const N = 40;
              for (let i = 0; i <= N; i++) {
                const pt = pathEl.getPointAtLength((i / N) * total);
                const dd = (pt.x - p.x) ** 2 + (pt.y - p.y) ** 2;
                if (dd < bestD) { bestD = dd; best = i / N; }
              }
              return best;
            };
            // Threshold: only treat as a drag (and call onMoveLabel) once the
            // pointer moves > 4px from the down position. This way a click or
            // dblclick on the label (no movement) does NOT shift the label.
            const downX = e.clientX;
            const downY = e.clientY;
            let dragging = false;
            const onMove = (ev: PointerEvent) => {
              if (!dragging) {
                if (Math.hypot(ev.clientX - downX, ev.clientY - downY) < 4) return;
                dragging = true;
                if (d.isAuto && d.onPromoteEdge) {
                  d.onPromoteEdge(id, d.computedSourceHandle, d.computedTargetHandle, bends);
                  d.isAuto = false;
                }
              }
              d.onMoveLabel?.(id, closestT(ev.clientX, ev.clientY));
            };
            const onUp = () => {
              window.removeEventListener('pointermove', onMove);
              window.removeEventListener('pointerup', onUp);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (d.isAuto && d.onPromoteEdge) {
              d.onPromoteEdge(id, d.computedSourceHandle, d.computedTargetHandle, bends);
              d.isAuto = false;
            }
            d.onContext?.(id, e.clientX, e.clientY);
          }}
          title={d.derivationTitle ?? `Flow along this edge. Double-click to inspect.`}
        >
          {formatNum(flow > 0 ? flow : d.weight)}
          {d.derivation && <span className="flow-edge-deriv">{d.derivation}</span>}
        </button>
        {/* Reconnect handles: small dots at each endpoint. Drag one to a
            different node side to re-anchor the edge (RF fires onReconnect). */}
        <ReconnectHandle
          edgeId={id}
          handleType="source"
          handleId={sourceHandleId}
          x={sourceX}
          y={sourceY}
          color={color}
          editable={!!d.editable}
        />
        <ReconnectHandle
          edgeId={id}
          handleType="target"
          handleId={targetHandleId}
          x={targetX}
          y={targetY}
          color={color}
          editable={!!d.editable}
        />
        {/* Bend points — draggable dots along the edge. Visible whenever the
            edge is editable AND has bends (NOT gated on selection — selection
            is for the modal, which is label-only). The visual varies by mode:
            sharp = square, smooth = hollow ring, default = filled dot. */}
        {d.editable && bends.length > 0 && bends.map((b, idx) => (
          <div
            key={`bend-${idx}`}
            // `nodrag nopan` are RF's escape-hatch classes: a pointerdown on
            // an element carrying them does NOT start a canvas pan/zoom. Without
            // these, grabbing the bend dot pans the viewport instead of moving
            // the bend (the dot is inside RF's pane DOM subtree).
            className={`edge-bend-dot mode-${b.mode} nodrag nopan`}
            style={{
              position: 'absolute',
              left: b.x,
              top: b.y,
              transform: 'translate(-50%, -50%)',
              borderColor: color,
              background: color,
            }}
            title={`${b.mode} bend — drag to move, right-click for options`}
            onPointerDown={(e) => {
              // Capture pointer so drags continue even when the cursor leaves
              // the dot. screenToFlowPosition converts screen → flow coords
              // using RF's current viewport (pan + zoom).
              e.stopPropagation();
              if (d.isAuto && d.onPromoteEdge) {
                d.onPromoteEdge(id, d.computedSourceHandle, d.computedTargetHandle, bends);
                d.isAuto = false;
              }
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              const onMove = (ev: PointerEvent) => {
                const p = rf.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
                d.onMoveBend?.(id, idx, p.x, p.y, bends);
              };
              const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
              };
              window.addEventListener('pointermove', onMove);
              window.addEventListener('pointerup', onUp);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (d.isAuto && d.onPromoteEdge) {
                d.onPromoteEdge(id, d.computedSourceHandle, d.computedTargetHandle, bends);
                d.isAuto = false;
              }
              d.onBendContext?.(id, idx, e.clientX, e.clientY);
            }}
          />
        ))}
      </EdgeLabelRenderer>
    </>
  );
};

export const FlowEdge = memo(FlowEdgeBase);
